import * as React from 'react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
    createBrowserRouter,
    RouterProvider
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// API
import { queryClient } from './api';

// Components
import { AppShell } from './components/AppShell';
import { StagingEnvBanner } from './components/StagingEnvBanner';
import { AdminRoute } from './components/ProtectedRoutes/AdminRoute';
import { ClientRoute } from './components/ProtectedRoutes/ClientRoute';

// Modals
import { HelpModal } from './components/Modals/HelpModal';
import { ContactModal } from './components/Modals/ContactModal';
import { ConfirmEmailModal } from './components/Modals/ConfirmEmailModal';

// Containers
import { Error } from './containers/Unauthenticated/Error';

// Contexts
import { AuthProvider } from './contexts/Auth';

// i18n
import './i18n';

// Routers
import { AdminRoutes } from './routeConfigs/Admin';
import { ClientRoutes } from './routeConfigs/Client';
import { UnauthenticatedRoutes } from './routeConfigs/Unauthenticated';

// Local
import pjson from '../package.json';

const {
    VITE_SHORT_SHA,
    VITE_SENTRY_DSN,
    VITE_SENTRY_RELEASE,
    VITE_SENTRY_ENV,
    VITE_SENTRY_TRACE_SAMPLE_RATE,
    VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE
} = import.meta.env;

const SentryRelease = `${VITE_SENTRY_RELEASE || 'DEVELOPMENT'}`;
Sentry.init({
    environment: VITE_SENTRY_ENV || '(undefined)',
    dsn: VITE_SENTRY_DSN || undefined,
    release: SentryRelease,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
        })
    ],
    tracePropagationTargets: [
        'localhost',
        'beep-itm-uat-api.onhive.dev' /*VITE_SENTRY_TRACE_PROP_TARGET ?? '', /^\//*/
    ],
    tracesSampleRate: VITE_SENTRY_TRACE_SAMPLE_RATE || 0.0,
    sampleRate: 1.0,
    replaysSessionSampleRate: VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE || 0.0,
    replaysOnErrorSampleRate: VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE || 0.0
});

Sentry.setTag('client_version', pjson.version);
Sentry.setTag('cloud_build_sha', VITE_SHORT_SHA);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
const router = sentryCreateBrowserRouter([
    {
        path: '/',
        element: (
            <React.Suspense fallback={<>...</>}>
                <AppShell />
            </React.Suspense>
        ),
        errorElement: <Error />,
        children: [
            {
                path: 'admin',
                element: <AdminRoute />,
                children: AdminRoutes
            },
            {
                path: 'client',
                element: <ClientRoute />,
                children: ClientRoutes
            },
            ...UnauthenticatedRoutes
        ]
    }
]);

export const App: React.FC = () => {
    return (
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>} showDialog>
            <MantineProvider
                theme={{
                    colors: {
                        brand: [
                            '#edf7f3',
                            '#deefe8',
                            '#deefe8',
                            '#deefe8',
                            '#deefe8',
                            '#deefe8',
                            '#168f5c',
                            '#168f5c',
                            '#168f5c'
                        ]
                    },
                    primaryColor: 'brand',
                    primaryShade: { light: 6, dark: 8 }
                }}
                withGlobalStyles
                withNormalizeCSS
            >
                <HelmetProvider>
                    <Notifications />
                    <ModalsProvider
                        modals={{
                            help: HelpModal,
                            contact: ContactModal,
                            confirmEmail: ConfirmEmailModal
                        }}
                        modalProps={{
                            centered: false,
                            overlayProps: {
                                zIndex: 202
                            }
                        }}
                    >
                        {VITE_SENTRY_ENV !== 'prd' ? <StagingEnvBanner /> : null}
                        <RouterProvider router={router} />
                        {/* <ReactQueryDevtools /> */}
                    </ModalsProvider>
                </HelmetProvider>
            </MantineProvider>
        </Sentry.ErrorBoundary>
    );
};

export const AuthenticationWrapperApp: React.FC = () => {
    return (
        <AuthProvider>
            <QueryClientProvider client={queryClient}>
                <App />
            </QueryClientProvider>
        </AuthProvider>
    );
};
